import React from "react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Tooltip,
} from "@mui/material";
import {
  DoubleArrow,
  CloseFullscreenSharp,
  OpenInFull,
} from "@mui/icons-material";
import { themes } from "services/constants";
import { EditShiftRotaConfig } from "./EditShiftRotaConfig";
import { EditShiftRotaShedule } from "./EditShiftRotaSchedule";
import { ShiftRotaPreview } from "../../ShiftRepresentation/ShiftRotaPreview";
import { tooltipStyleProps } from "services/constants/Styles";
import { AnimatedConnector } from "components/CustomComponents/Stepper";
import { CustomStepIcon } from "components/CustomComponents/Stepper";
import { useShiftRota } from "../../ShiftRotaContext";

export const EditShiftRota = ({
  closeEditPanel,
  onEditClosePanel,
  reCallApi,
  setOpenSidebar,
  setHandleLoader,
  loader,
}) => {
  const {
    configRef,
    noOfSft,
    activeStep,
    setActiveStep,
    setFormData,
    expandPage,
  } = useShiftRota();

  const handleNext = (value) => {
    setActiveStep(value);
  };

  const handleBack = (value) => {
    setActiveStep(value);
  };

  const handleDataUpdate = (step, data) => {
    setFormData((prev) => ({
      ...prev,
      [step]: { ...prev[step], ...data },
    }));
  };

  const steps = [
    { label: "Configuration" },
    { label: "Schedule" },
    { label: "Preview" },
  ];

  const filteredSteps = steps.filter(
    (step) => !(noOfSft && step.label === "Schedule")
  );

  const renderStepContent = (activeStep) => {
    let step = noOfSft && activeStep === 1 ? 2 : activeStep;
    switch (step) {
      case 0:
        return (
          <EditShiftRotaConfig
            handleNext={handleNext}
            onUpdate={(data) => handleDataUpdate("config", data)}
          />
        );
      case 1:
        return (
          <EditShiftRotaShedule
            handleNext={handleNext}
            handleBack={handleBack}
            onUpdate={(data) => handleDataUpdate("schedule", data)}
            showFullPage={expandPage}
          />
        );
      case 2:
        return (
          <ShiftRotaPreview
            handleBack={handleBack}
            reCallApi={reCallApi}
            loader={loader}
            isEdit={true}
            isCreate={false}
            onClose={closeEditPanel}
            setHandleLoader={setHandleLoader}
          />
        );
      default:
        return "Unknown step";
    }
  };
  return (
    <Box
      sx={{
        height: expandPage ? "90vh" : "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          borderBottom: `1px solid ${themes?.miliedLightGray}`,
          borderRadius: 0,
        }}
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          sx={{ padding: 1, backgroundColor: themes?.primary }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              borderRadius: "50%",
            }}
          >
            {!expandPage ? (
              <>
                <Box sx={{ ml: 1 }}>
                  <IconButton
                    onClick={closeEditPanel}
                    sx={{
                      color: themes?.grayColor,
                      "&:hover": {
                        backgroundColor: themes?.whiteColor,
                      },
                    }}
                  >
                    <DoubleArrow />
                  </IconButton>
                </Box>
                <Tooltip
                  title="Open in full page"
                  arrow
                  slotProps={tooltipStyleProps}
                >
                  <Box>
                    <IconButton
                      onClick={() => {
                        configRef.current?.saveFormValues();
                        onEditClosePanel("openInFullPage");
                        setOpenSidebar(false);
                      }}
                      sx={{
                        color: themes?.grayColor,
                        "&:hover": {
                          backgroundColor: themes?.whiteColor,
                        },
                      }}
                    >
                      <OpenInFull />
                    </IconButton>
                  </Box>
                </Tooltip>
              </>
            ) : (
              <Box sx={{ ml: 0.5 }}>
                <Tooltip
                  title="Back to side panel"
                  arrow
                  slotProps={tooltipStyleProps}
                >
                  <IconButton
                    onClick={() => {
                      configRef.current?.saveFormValues();
                      onEditClosePanel("");
                    }}
                    sx={{
                      color: themes?.grayColor,
                      "&:hover": {
                        backgroundColor: themes?.whiteColor,
                      },
                    }}
                  >
                    <CloseFullscreenSharp />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>

          <Box sx={{ ml: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Edit Shift Rota
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Plan and assign recurring shifts with defined rotation rules.
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Box sx={{ flexGrow: 1, overflow: "auto" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ mt: 3 }}>
            <Stepper
              activeStep={activeStep}
              alternativeLabel
              connector={<AnimatedConnector />}
              sx={{ mb: 2 }}
            >
              {filteredSteps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel
                    StepIconComponent={CustomStepIcon}
                    sx={{
                      "& .MuiStepLabel-label.Mui-active": {
                        color: "#00D4AA",
                        fontWeight: "bold",
                      },
                      "& .MuiStepLabel-label.Mui-completed": {
                        color: "#00D4AA",
                      },
                    }}
                  >
                    {step.label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
          <Box
            sx={{
              borderBottom: `1px solid ${themes?.miliedLightGray}`,
              borderRadius: 0,
            }}
          ></Box>
          <Box>{renderStepContent(activeStep)}</Box>
        </Box>
      </Box>
    </Box>
  );
};
